// mutation.ts – appels HTTP bruts vers les APIs /public/demos (prospect)

import axios from 'axios';
import { handleAxiosError } from '@/libs/utils';
import {
  normalizePublicDemosMeta,
  type ApiResult,
  type FetchAvailabilityParams,
  type PublicDemoAvailabilityResponse,
  type PublicDemoCreateSessionBody,
  type PublicDemoPatchSessionBody,
  type PublicDemoPatchSessionResponse,
  type PublicDemoSession,
  type PublicDemosMetaResponse,
  type PublicDemoSessionsResponse,
} from '../types';

const DEBUG_PUBLIC_DEMOS = process.env.NODE_ENV !== 'production';
const LOCAL_PUBLIC_DEMOS_API = '/api/public/demos';

// NOTE: on passe par des routes Next `/api/public/demos/*` (proxy serveur) pour éviter CORS.
// Le base URL `PROSPECT_PUBLIC_DEMOS_BASE_URL` est donc moins critique côté navigateur.

function toResultError(err: unknown, fallback: string): ApiResult<never> {
  if (!axios.isAxiosError(err)) {
    if (DEBUG_PUBLIC_DEMOS) {
      // eslint-disable-next-line no-console
      console.error('[public-demos] Erreur non-axios', err);
    }
    return { ok: false, status: 0, error: 'Erreur réseau.' };
  }
  if (DEBUG_PUBLIC_DEMOS) {
    const status = err.response?.status ?? 0;
    const url = err.config?.url;
    const method = err.config?.method;
    const responseData = err.response?.data;
    const isBrowser = typeof window !== 'undefined';
    const online =
      isBrowser && typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
        ? navigator.onLine
        : undefined;
    // eslint-disable-next-line no-console
    console.error('[public-demos] Erreur axios', {
      method,
      url,
      status,
      code: err.code,
      message: err.message,
      responseData,
      online,
      hint:
        status === 0
          ? 'status=0 => requête bloquée/échouée avant réponse HTTP (souvent CORS, proxy, DNS, certif, adblock).'
          : undefined,
    });
  }
  return {
    ok: false,
    status: err.response?.status ?? 0,
    error: handleAxiosError(err, fallback),
  };
}

export async function fetchPublicDemosMeta(): Promise<ApiResult<PublicDemosMetaResponse>> {
  // IMPORTANT: via API route Next pour éviter CORS côté navigateur.
  const url = `${LOCAL_PUBLIC_DEMOS_API}/meta`;
  try {
    if (DEBUG_PUBLIC_DEMOS) {
      // eslint-disable-next-line no-console
      console.log('[public-demos] GET meta', { url });
    }
    const { data } = await axios.get<unknown>(url, {
      headers: { accept: 'application/json' },
    });
    const normalized = normalizePublicDemosMeta(data);
    if (DEBUG_PUBLIC_DEMOS) {
      // eslint-disable-next-line no-console
      console.log('[public-demos] meta OK', {
        user_types_count: normalized.user_types?.length ?? 0,
        meeting_platforms_count: normalized.meeting_platforms?.length ?? 0,
        sample_user_types: normalized.user_types?.slice(0, 5) ?? [],
      });
    }
    return { ok: true, data: normalized };
  } catch (err) {
    return toResultError(err, 'Impossible de charger les métadonnées de démo.');
  }
}

export async function fetchPublicDemosAvailability(
  params: FetchAvailabilityParams,
): Promise<ApiResult<PublicDemoAvailabilityResponse>> {
  const url = `${LOCAL_PUBLIC_DEMOS_API}/availability`;
  try {
    const { data } = await axios.get<PublicDemoAvailabilityResponse>(
      url,
      {
        headers: { accept: 'application/json' },
        params,
      },
    );
    return { ok: true, data };
  } catch (err) {
    return toResultError(err, 'Impossible de charger les disponibilités de démo.');
  }
}

export async function fetchPublicDemoSessions(
  limit = 500,
): Promise<ApiResult<PublicDemoSessionsResponse>> {
  const url = `${LOCAL_PUBLIC_DEMOS_API}/sessions`;
  try {
    const { data } = await axios.get<PublicDemoSessionsResponse>(url, {
      headers: { accept: 'application/json' },
      params: { limit },
    });
    return { ok: true, data };
  } catch (err) {
    return toResultError(err, 'Impossible de charger les sessions de démo.');
  }
}

export async function createPublicDemoSession(
  body: PublicDemoCreateSessionBody,
): Promise<ApiResult<PublicDemoSession>> {
  const url = `${LOCAL_PUBLIC_DEMOS_API}/sessions`;
  try {
    const { data } = await axios.post<PublicDemoSession>(url, body, {
      headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    });
    return { ok: true, data };
  } catch (err) {
    return toResultError(err, 'Impossible de créer la session de démo.');
  }
}

export async function patchPublicDemoSession(
  sessionId: string,
  body: PublicDemoPatchSessionBody,
): Promise<ApiResult<PublicDemoPatchSessionResponse>> {
  const url = `${LOCAL_PUBLIC_DEMOS_API}/sessions/${encodeURIComponent(sessionId)}`;
  try {
    const { data } = await axios.patch<PublicDemoPatchSessionResponse>(
      url,
      body,
      { headers: { accept: 'application/json', 'Content-Type': 'application/json' } },
    );
    return { ok: true, data };
  } catch (err) {
    return toResultError(err, 'Impossible de mettre à jour la session de démo.');
  }
}

export async function deletePublicDemoSession(
  sessionId: string,
): Promise<ApiResult<null>> {
  const url = `${LOCAL_PUBLIC_DEMOS_API}/sessions/${encodeURIComponent(sessionId)}`;
  try {
    await axios.delete(url, {
      headers: { accept: 'application/json' },
    });
    return { ok: true, data: null };
  } catch (err) {
    return toResultError(err, 'Impossible de supprimer la session de démo.');
  }
}

